import React, { useEffect, useState } from 'react';
import { Form, Row, Col, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSync, faExclamationTriangle, faCheckCircle,
  faClock, faTimesCircle, faSave, faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import {
  obtenerPublicacionWeb,
  guardarPublicacionWeb,
  sincronizarWooCommerce,
} from '../../../../actions/modulosActions';

const MODALIDADES   = [{ value: 'en_vivo', label: 'En vivo' }, { value: 'grabado', label: 'Grabado' }];
const CATEGORIAS    = ['Tributación', 'Contabilidad', 'Minería', 'Laboral', 'Otros'];

const estadoBadge = (status) => {
  const map = {
    publicado:    { variant: 'success',   icon: faCheckCircle,       label: 'Publicado'     },
    error:        { variant: 'danger',    icon: faTimesCircle,       label: 'Error'         },
    pendiente:    { variant: 'warning',   icon: faClock,             label: 'Pendiente'     },
    no_publicado: { variant: 'secondary', icon: faExclamationTriangle, label: 'No publicado' },
  };
  const cfg = map[status] || map['no_publicado'];
  return (
    <Badge variant={cfg.variant} style={{ fontSize: 13, padding: '5px 10px' }}>
      <FontAwesomeIcon icon={cfg.icon} className="mr-1" />
      {cfg.label}
    </Badge>
  );
};

const initialForm = {
  publicar_catalogo: 0,
  precio_web:        '',
  imagen_web:        '',
  modalidad:         '',
  categoria_web:     '',
  descripcion_corta: '',
};

const TabPublicacionWeb = ({ idModulo, nombreModulo }) => {
  const dispatch   = useDispatch();
  const [pub,      setPub]      = useState(null);
  const [form,     setForm]     = useState(initialForm);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [syncing,  setSyncing]  = useState(false);
  const [errores,  setErrores]  = useState([]);

  useEffect(() => {
    if (!idModulo) return;
    setLoading(true);
    dispatch(obtenerPublicacionWeb(idModulo)).then(data => {
      if (data) {
        setPub(data);
        setForm({
          publicar_catalogo: data.publicar_catalogo || 0,
          precio_web:        data.precio_web        || '',
          imagen_web:        data.imagen_web        || '',
          modalidad:         data.modalidad         || '',
          categoria_web:     data.categoria_web     || '',
          descripcion_corta: data.descripcion_corta || '',
        });
      }
      setLoading(false);
    });
  }, [idModulo]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  };

  const handleGuardar = async () => {
    setSaving(true);
    setErrores([]);
    const ok = await dispatch(guardarPublicacionWeb(idModulo, form));
    if (ok) {
      const data = await dispatch(obtenerPublicacionWeb(idModulo));
      if (data) setPub(data);
    }
    setSaving(false);
  };

  const handleSincronizar = async () => {
    setSyncing(true);
    setErrores([]);
    const result = await dispatch(sincronizarWooCommerce(idModulo));
    if (result.ok) {
      const data = await dispatch(obtenerPublicacionWeb(idModulo));
      if (data) setPub(data);
    } else {
      setErrores(result.errores || [result.error || 'Error al sincronizar']);
    }
    setSyncing(false);
  };

  if (loading) {
    return <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>;
  }

  const syncStatus = pub?.sync_status || 'no_publicado';

  return (
    <div style={{ padding: '20px 4px' }}>

      {/* Estado actual */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '12px 16px', background: '#f8f9fa', borderRadius: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Estado de publicación</div>
          {estadoBadge(syncStatus)}
          {pub?.last_sync_at && (
            <span style={{ marginLeft: 12, fontSize: 11, color: '#aaa' }}>
              Última sync: {pub.last_sync_at}
            </span>
          )}
        </div>
        {pub?.public_url && (
          <a href={pub.public_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-success">
            <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" /> Ver en web
          </a>
        )}
      </div>

      {/* Error de sincronización previo */}
      {syncStatus === 'error' && pub?.sync_error_message && (
        <Alert variant="danger" style={{ fontSize: 12 }}>
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
          {pub.sync_error_message}
        </Alert>
      )}

      {/* Errores de validación */}
      {errores.length > 0 && (
        <Alert variant="warning">
          <strong>Corrige estos campos antes de publicar:</strong>
          <ul className="mb-0 mt-1">
            {errores.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </Alert>
      )}

      {/* Formulario */}
      <Form>
        {/* Switch publicar */}
        <Form.Group>
          <Form.Check
            type="switch"
            id="publicar_catalogo"
            name="publicar_catalogo"
            label={<strong>Publicar en catálogo web</strong>}
            checked={!!form.publicar_catalogo}
            onChange={handleChange}
          />
          <Form.Text className="text-muted">
            Al activar y sincronizar, el curso aparecerá en <em>inedi.edu.pe</em>
          </Form.Text>
        </Form.Group>

        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Precio web (S/.)</Form.Label>
              <Form.Control
                type="number"
                name="precio_web"
                value={form.precio_web}
                onChange={handleChange}
                placeholder="Ej: 350"
                min="0"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Modalidad</Form.Label>
              <Form.Control
                as="select"
                name="modalidad"
                value={form.modalidad}
                onChange={handleChange}
              >
                <option value="">-- Seleccionar --</option>
                {MODALIDADES.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Categoría web</Form.Label>
              <Form.Control
                as="select"
                name="categoria_web"
                value={form.categoria_web}
                onChange={handleChange}
              >
                <option value="">-- Seleccionar --</option>
                {CATEGORIAS.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>URL imagen principal</Form.Label>
              <Form.Control
                type="url"
                name="imagen_web"
                value={form.imagen_web}
                onChange={handleChange}
                placeholder="https://..."
              />
              {form.imagen_web && (
                <img
                  src={form.imagen_web}
                  alt="preview"
                  style={{ marginTop: 8, maxHeight: 80, borderRadius: 4, border: '1px solid #dee2e6' }}
                  onError={e => e.target.style.display = 'none'}
                />
              )}
            </Form.Group>
          </Col>
        </Row>

        <Form.Group>
          <Form.Label>Descripción corta <small className="text-muted">(aparece en la card del catálogo)</small></Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="descripcion_corta"
            value={form.descripcion_corta}
            onChange={handleChange}
            placeholder="Descripción breve del curso para mostrar en el catálogo web..."
            maxLength={300}
          />
          <Form.Text className="text-muted">{form.descripcion_corta.length}/300 caracteres</Form.Text>
        </Form.Group>

        {/* ID WooCommerce (informativo) */}
        {pub?.woo_product_id && (
          <Form.Group>
            <Form.Label>ID WooCommerce</Form.Label>
            <Form.Control type="text" value={pub.woo_product_id} readOnly style={{ background: '#f8f9fa', fontFamily: 'monospace' }} />
          </Form.Group>
        )}
      </Form>

      {/* Botones */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid #dee2e6' }}>
        <Button variant="primary" onClick={handleGuardar} disabled={saving}>
          {saving
            ? <><Spinner animation="border" size="sm" className="mr-1" /> Guardando...</>
            : <><FontAwesomeIcon icon={faSave} className="mr-1" /> Guardar configuración</>
          }
        </Button>
        <Button
          variant={syncStatus === 'error' ? 'danger' : 'success'}
          onClick={handleSincronizar}
          disabled={syncing}
        >
          {syncing
            ? <><Spinner animation="border" size="sm" className="mr-1" /> Sincronizando...</>
            : <><FontAwesomeIcon icon={faSync} className="mr-1" />
                {syncStatus === 'error' ? 'Reintentar publicación' : 'Sincronizar con WordPress'}</>
          }
        </Button>
      </div>
    </div>
  );
};

export default TabPublicacionWeb;
